import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  createOriginRequest,
  createOriginSuccess,
  updateOriginSuccess,
} from "../../actions/origin";
import { createorUpdateOrigin, updateOriginContacts } from "../../api/origin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import api from "../../helpers/util";

import OriginDesc from "./originDesc";
import ErrorAlertBox from "../general/ErrorAlertBox";

const emptyContact = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  includeinemail: 1,
};

const CreateOriginData = ({
  handleClose,
  temperature,
  units,
  packTypes,
  storeTypes,
  locations,
  species,
  companies,
  originId,
  deliveryMethods,
}) => {
  const dispatch = useDispatch();

  const [originForm, setOriginForm] = useState({
    name: "",
    originof: null,
  });

  const [contacts, setContacts] = useState([{ ...emptyContact }]);

  const [validationErrors, setValidationErrors] = useState({
    name: "",
    contacts: [],
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isAlertVisible, setAlertVisible] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  // Index of the contact awaiting delete confirmation (null = no dialog)
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  // Fetch existing origin data when updating
  useEffect(() => {
    if (originId) {
      const fetchOrigin = async () => {
        try {
          const {
            data: { result },
          } = await api.get(`/origins/${originId}`);

          setOriginForm({
            name: result.name,
            originof: result.originof,
          });

          if (Array.isArray(result.contacts) && result.contacts.length > 0) {
            setContacts(result.contacts);
          } else if (result.contactfirstname || result.email) {
            // Fall back to the legacy single-contact fields so existing
            // data isn't lost for origins that haven't been re-saved yet.
            setContacts([
              {
                firstname: result.contactfirstname || "",
                lastname: "",
                email: result.email ? result.email.trim() : "",
                phone: "",
                includeinemail: 1,
              },
            ]);
          } else {
            setContacts([{ ...emptyContact }]);
          }
        } catch (error) {
          setErrorMessage("Error fetching origin details.");
        }
      };
      fetchOrigin();
    }
  }, [originId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setOriginForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleContactChange = (index, field, value) => {
    setContacts((prev) =>
      prev.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact
      )
    );
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { ...emptyContact }]);
  };

  // Trash icon click: open confirmation dialog instead of deleting straight away
  const removeContact = (index) => {
    setPendingDeleteIndex(index);
  };

  const cancelRemoveContact = () => setPendingDeleteIndex(null);

  // Confirmed: delete immediately. When editing an existing origin this
  // persists to the DB right away (bulk-replace with the remaining
  // contacts) - no separate Update click needed. In create mode there's
  // nothing in the DB yet, so it's local state only.
  const confirmRemoveContact = () => {
    const index = pendingDeleteIndex;
    setPendingDeleteIndex(null);
    if (index === null) return;

    const remaining = contacts.filter((_, i) => i !== index);

    if (originId) {
      updateOriginContacts(originId, remaining)
        .then((updatedContacts) => {
          setContacts(updatedContacts);
          dispatch(
            updateOriginSuccess({ id: originId, contacts: updatedContacts })
          );
          setIsError(false);
          setSuccessMessage("Contact deleted");
          setShowMessage(true);
        })
        .catch(() => {
          setIsError(true);
          setErrorMessage("Error deleting contact");
          setShowMessage(true);
        });
    } else {
      setContacts(remaining);
    }
  };

  const validateOrigin = () => {
    const nameError = !originForm.name
      ? "Please enter a valid origin name"
      : "";

    const contactErrors = contacts.map((contact) => ({
      firstname: !contact.firstname ? "First name is required" : "",
      email:
        contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)
          ? "Please enter a valid email address"
          : "",
    }));

    return { name: nameError, contacts: contactErrors };
  };

  const handleCreateOrigin = () => {
    const errors = validateOrigin();
    const hasErrors =
      Boolean(errors.name) ||
      errors.contacts.some((c) => c.firstname || c.email);
    setValidationErrors(errors);

    if (hasErrors) return;

    dispatch(createOriginRequest());

    if (originId) {
      // Existing origin: name is read-only, so contacts are the only
      // thing this dialog can update.
      updateOriginContacts(originId, contacts)
        .then((updatedContacts) => {
          dispatch(
            updateOriginSuccess({ id: originId, contacts: updatedContacts })
          );
          setIsError(false);
          setSuccessMessage("Origin updated successfully");
          setShowMessage(true);
          setTimeout(() => handleClose(), 1500);
        })
        .catch(() => {
          setIsError(true);
          setShowMessage(true);
          setErrorMessage("Error in updating the origin");
        });
    } else {
      createorUpdateOrigin({ ...originForm, contacts }, null)
        .then((newOrigin) => {
          setOriginForm({ name: "", originof: null });
          setContacts([{ ...emptyContact }]);
          dispatch(createOriginSuccess(newOrigin));
          setIsError(false);
          setSuccessMessage("Origin created successfully");
          setShowMessage(true);
          setTimeout(() => handleClose(), 1500);
        })
        .catch(() => {
          setIsError(true);
          setShowMessage(true);
          setErrorMessage("Error in creating the origin");
        });
    }
  };

  const closeAlert = () => setAlertVisible(false);

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          overflow: "auto",
          height: "550px",
          minHeight: "550px",
          maxHeight: "550px",
        }}>
        <DialogTitle>
          <div className="mb-2 ms-3">
            {originId ? "Update Origin" : "Create Origin"}
          </div>
        </DialogTitle>

        <DialogContent>
          <div
            className="create-origin pb-0"
            style={{ height: "335px", overflowY: "auto" }}>
            <div
              className="tab-content pb-0"
              id="pills-tabContent"
              style={{ height: "335px" }}>
              <div
                className="tab-pane fade show active"
                id="pills-desc"
                role="tabpanel"
                aria-labelledby="pills-desc-tab"
                style={{ height: "300px" }}>
                <OriginDesc
                  handleChange={handleChange}
                  originForm={originForm}
                  validationErrors={validationErrors}
                  deliveryMethods={deliveryMethods}
                  contacts={contacts}
                  onContactChange={handleContactChange}
                  onAddContact={addContact}
                  onRemoveContact={removeContact}
                  originId={originId}
                />
              </div>

              <div
                className="tab-pane fade"
                id="pills-spec"
                role="tabpanel"
                aria-labelledby="pills-spec-tab"
                style={{ height: "438px" }}>
                Format Settings
              </div>

              <div
                className="tab-pane fade"
                id="pills-sale"
                role="tabpanel"
                aria-labelledby="pills-sale-tab">
                <div style={{ height: "438px" }}>Sales History</div>
              </div>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCreateOrigin} color="primary">
            {originId ? "Update" : "Create"}
          </Button>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete contact confirmation */}
      <Dialog open={pendingDeleteIndex !== null} onClose={cancelRemoveContact}>
        <DialogTitle>Delete contact?</DialogTitle>
        <DialogContent>
          {pendingDeleteIndex !== null && (
            <span>
              {`Delete ${
                [
                  contacts[pendingDeleteIndex]?.firstname,
                  contacts[pendingDeleteIndex]?.lastname,
                ]
                  .filter(Boolean)
                  .join(" ") || "this contact"
              }${
                contacts[pendingDeleteIndex]?.email
                  ? ` (${contacts[pendingDeleteIndex].email})`
                  : ""
              }?`}
              {originId ? " This will be deleted immediately." : ""}
            </span>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemoveContact} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmRemoveContact} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {showMessage && (
        <Snackbar
          open={showMessage}
          autoHideDuration={2000}
          onClose={() => setShowMessage(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}>
          <Alert severity={isError ? "error" : "success"} variant="filled">
            {isError ? errorMessage : successMessage}
          </Alert>
        </Snackbar>
      )}

      {isAlertVisible && (
        <ErrorAlertBox message={alertMsg} onClose={closeAlert} />
      )}
    </>
  );
};

export default CreateOriginData;
